import dayjs from 'dayjs';
import 'dayjs/locale/ja'; // 日本語ロケール
import customParseFormat from 'dayjs/plugin/customParseFormat'; // カスタムパース用プラグイン

// カスタムパース用プラグインを追加
dayjs.extend(customParseFormat);

const DATE_FORMATS = {
  // 標準的な日付フォーマット
  standard: [
    'YYYY-MM-DD',    // 2024-02-05
    'YYYY-M-D',      // 2024-2-5
    'YYYY/MM/DD',    // 2024/02/05
    'YYYY/M/D',      // 2024/2/5
  ],
  
  // 日本語フォーマット
  japanese: [
    'YYYY年MM月DD日',  // 2024年02月05日
    'YYYY年M月D日',    // 2024年2月5日
    'YYYY年MM月',
    'YYYY年M月',
  ],
  
  // 時刻付きフォーマット
  withTime: [
    'YYYY-MM-DD HH:mm:ss',       // 2024-02-05 14:30:00
    'YYYY-MM-DD HH:mm',          // 2024-02-05 14:30
    'YYYY/MM/DD HH:mm:ss',       // 2024/02/05 14:30:00
    'YYYY/MM/DD HH:mm',          // 2024/02/05 14:30
    'YYYY年MM月DD日 HH時mm分ss秒', // 2024年02月05日 14時30分00秒
    'YYYY年MM月DD日 HH時mm分',     // 2024年02月05日 14時30分
  ],
  
  // ISO 8601フォーマット
  iso8601: [
    'YYYY-MM-DDTHH:mm:ss.SSSZ',  // 2024-02-05T14:30:00.000+09:00
    'YYYY-MM-DDTHH:mm:ssZ',      // 2024-02-05T14:30:00+09:00
    'YYYY-MM-DDTHH:mmZ',         // 2024-02-05T14:30+09:00
  ],
  
  // 曜日付きフォーマット
  withWeekday: [
    'YYYY-MM-DD(ddd)',           // 2024-02-05(火)
    'YYYY年MM月DD日(ddd)',        // 2024年02月05日(火)
    'YYYY年M月D日(ddd)',          // 2024年2月5日(火)
    'YYYY-MM-DD dddd',           // 2024-02-05 火曜日
    'YYYY年MM月DD日 dddd',        // 2024年02月05日 火曜日
  ],
  
  // その他の一般的なフォーマット
  others: [
    'MMM Do YYYY',               // Feb 5th 2024
    'YYYY.MM.DD',               // 2024.02.05
    'YYYY.M.D',                 // 2024.2.5
    'DD-MM-YYYY',               // 05-02-2024 (英国式)
    'D-M-YYYY',                 // 5-2-2024 (英国式)
    'MM/DD/YYYY',               // 02/05/2024 (米国式)
    'M/D/YYYY',                 // 2/5/2024 (米国式)
  ]
};

export function parseAnyDate(dateString: string): dayjs.Dayjs | null {
  const dayJsDate = dayjs(dateString);
  if (dayJsDate.isValid()) {
    return dayJsDate;
  }
  
  // 日本語の日付表記をパース
  const japaneseDate = parseJapaneseDate(dateString);
  
  if (japaneseDate) {
    return dayjs(japaneseDate);
  }
  
  // すべてのフォーマットを1つの配列に結合
  const formats = [
    ...DATE_FORMATS.standard,
    ...DATE_FORMATS.japanese,
    ...DATE_FORMATS.withTime,
    ...DATE_FORMATS.iso8601,
    ...DATE_FORMATS.withWeekday,
    ...DATE_FORMATS.others
  ];
  
  // 各フォーマットを試行
  for (const format of formats) {
    const parsed = dayjs(dateString, format);
    if (parsed.isValid()) {
      return parsed;
    }
  }
  
  // どのフォーマットでもパースできない場合
  return null;
}

function parseJapaneseDate(dateString: string): Date | undefined {
  const getEraNameList = function (type: number): string[] {
    const list: string[] = [];
    for (let i = 0; i < eraTypes.length; i++) {
      const obj: EraType = eraTypes[i];
      if (type === 1) list.push(obj.name);
      if (type === 2) list.push(obj.name_short);
    }
    
    return list;
  }
  
  const regExp = new RegExp(
    "^([" +
    getEraNameList(2).join("|") +
    "]+)(\\d{1,2})\\/?(\\d{1,2})\\/?(\\d{1,2})+$"
  );
  if (dateString.match(regExp)) {
    const data: RegExpMatchArray | null = dateString.match(regExp);
    for (let i = 0; i < eraTypes.length; i++) {
      const obj: EraType = eraTypes[i];
      if (data![1] == obj.name_short) {
        const newYear =
          parseInt(data![2], 10) + (obj.timestamp.getUTCFullYear() - 1);
        return new Date(
          newYear,
          parseInt(data![3], 10) - 1,
          parseInt(data![4], 10),
          0,
          0,
          0,
          0
        );
      }
    }
  }
  // format 2001年02月03日
  if (dateString.match(/^\d{4}[年]\d{1,2}[月]\d{1,2}[日]/)) {
    const data: RegExpMatchArray | null = dateString.match(
      /^(\d{4})[年](\d{1,2})[月](\d{1,2})[日]/
    );
    return new Date(
      parseInt(data![1], 10),
      parseInt(data![2], 10) - 1,
      parseInt(data![3], 10),
      0,
      0,
      0,
      0
    );
  }
  // format: 平成21年2月3日
  const regExp2 = new RegExp(
    "^([" +
    getEraNameList(1).join("|") +
    "]+)(\\d{1,3})年(\\d{1,2})月(?:(\\d{1,2})日)?"
  );
  if (dateString.match(regExp2)) {
    const data: RegExpMatchArray | null = dateString.match(regExp2);
    for (let i = 0; i < eraTypes.length; i++) {
      const obj: EraType = eraTypes[i];
      if (data![1] == obj.name) {
        const newYear =
          parseInt(data![2], 10) + (obj.timestamp.getUTCFullYear() - 1);
        return new Date(
          newYear,
          parseInt(data![3], 10) - 1,
          parseInt(data?.[4] ?? '0', 10),
          0,
          0,
          0,
          0
        );
      }
    }
  }
}

interface EraType {
  name: string;
  name_short: string;
  timestamp: Date;
}

const eraTypes: EraType[] = [
  {
    name: "令和",
    name_short: "R",
    timestamp: new Date("2019-05-01T00:00:00"),
  },
  {
    name: "平成",
    name_short: "H",
    timestamp: new Date("1989-01-08T00:00:00"),
  },
  {
    name: "昭和",
    name_short: "S",
    timestamp: new Date("1926-12-25T00:00:00"),
  },
  {
    name: "大正",
    name_short: "T",
    timestamp: new Date("1912-07-30T00:00:00"),
  },
  {
    name: "明治",
    name_short: "M",
    timestamp: new Date("1868-01-25T00:00:00"),
  },
];
